import React, { useEffect, useState } from "react";
import NotificationModal from "../../../components/modal/NotificationModal";
import logo from "../../../images/TestAssessify.png";
import { ClipboardDocumentIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { api } from "../../../api";
import { useMutation } from "react-query";

const EmailVerificationModal = ({ email, setShowNotification, showSkip=true }) => {
  const { mutateAsync: requestVerificationMutation } = useMutation(api.requestVerification);
  const navigate = useNavigate();

  const [verificationData, setVerificationData] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [status, setStatus] = useState("loading"); 
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    handleSelfVerification();
  }, []);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("verify@testassessify.com");
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 1500);
  };

  const handleCopyCode = () => {
    if (verificationData?.verification_code) {
      navigator.clipboard.writeText(verificationData.verification_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 1500);
    }
  };

 const handleSelfVerification = async () => {
  try {
    setStatus("loading");
    setErrorMessage("");
    toast.dismiss();
   // toast.loading("Requesting verification code...");

    const response = await requestVerificationMutation({
      product_id: '51b9fc4a-6b4e-4a3d-90e1-098a52edec89' || process.env.REACT_APP_PRODUCT_ID,
      channel: "email",
      email,
    });

    toast.dismiss();

    const code = response?.verification_code;
    const instructions = response?.instructions;

    if (!code || !instructions) {
      setStatus("error");
      setErrorMessage("No verification code received ❌");
      throw new Error("No verification code received");
    }

   setVerificationData({ verification_code: code, instructions });
    setStatus("success");
   // toast.success("Verification code received 🎉");
  } catch (error) {
    if(error?.response?.status === 400){
       if(error?.response?.data?.error?.includes('already verified')){
         return
       }
    }
    setStatus("error");
    setVerificationData(null);
    const message =
      error.response?.data?.message ||
      error.message ||
      "Self verification failed ❌";
    setErrorMessage(message);
    toast.dismiss();
    toast.error(message, { duration: 6000 });
  }
};


  const handleMailClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    toast.loading("Redirecting to login page...");
    setTimeout(() => {
      navigate("/login");
    }, 3000);
  };

  const getMailtoLink = () => {
    const subject = "Email Verification Code";
    const body = verificationData?.verification_code || "";
    return `mailto:verify@testassessify.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const getGmailLink = () => {
    const subject = "Email Verification Code";
    const body = verificationData?.verification_code || "";
    return `https://mail.google.com/mail/?view=cm&fs=1&to=verify@testassessify.com&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const getOutlookLink = () => {
    const subject = "Email Verification Code";
    const body = verificationData?.verification_code || "";
    return `https://outlook.office.com/mail/deeplink/compose?to=verify@testassessify.com&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSkip = () => {
    setShowNotification(false);
  };

  if(!verificationData) return

  return (
    <NotificationModal onClose={() => setShowNotification(false)} enableClose={true}>
     
      <div className="h-full -mt-8 -ml-3 border-b border-gray-200 pb-4">
        <img src={logo} alt="Test Assessify" className="w-56 h-full object-contain" />
      </div>

      <h5 data-test-id="email-verification-modal-title" className="text-3xl mt-3 text-darkBlue font-semibold">
        Email Verification Required
      </h5>

      <p className="text-lg text-gray-700 mt-0">
        To continue, please confirm your email address is real.
      </p>

      <div className="mt-6 w-full bg-green-50 border-l-4 border-primary p-4 rounded-md">
        <p className="font-semibold text-lg text-gray-800">Important</p>
        <p className="text-gray-700 mt-2">
          Send an email from your registered address{" "}
          <strong>{email}</strong> to{" "}
          <span className="flex items-center gap-1 inline-flex">
            <strong className="text-primary">verify@testassessify.com</strong>
            {copiedEmail ? (
              <CheckCircleIcon
                className="w-5 h-5 text-primary transition-transform duration-300"
                title="Copied!"
              />
            ) : (
              <ClipboardDocumentIcon
                className="w-5 h-5 text-gray-600 cursor-pointer hover:text-primary"
                onClick={handleCopyEmail}
                title="Copy email address"
              />
            )}
          </span>{" "}
          and include this code:
        </p>

        <div className="flex items-center gap-3 mt-3">
         <h3 className={`text-2xl font-bold ${
  status === "error"
    ? "text-red-600"
    : status === "loading"
    ? "text-gray-600"
    : "text-primary"
}`}>
  {status === "loading"
    ? "Requesting code..."
    : status === "error"
    ? errorMessage
    : verificationData?.verification_code}
</h3>

          {copiedCode ? (
            <CheckCircleIcon
              className="w-6 h-6 text-primary transition-transform duration-300"
              title="Copied!"
            />
          ) : (
            <ClipboardDocumentIcon
              className="w-6 h-6 text-gray-600 cursor-pointer hover:text-primary"
              onClick={handleCopyCode}
              title="Copy code"
            />
          )}
        </div>
      </div>

      <div className="mt-6">
        <h6 className="font-semibold text-lg text-darkBlue">Option 1 — Fastest</h6>
        <p className="mb-2">
          Click any of the options below to send your verification email automatically.
        </p>
        <p className="text-sm text-orange-500 mb-3 italic">
          The sender will automatically be your currently active email account.
        </p>

        <div className="flex gap-4 flex-wrap">
          <button
            onClick={() => handleMailClick(getMailtoLink())}
            className="bg-primary py-2 px-4 text-white rounded-lg"
          >
            Open Local Mail App
          </button>

          <button
            onClick={() => handleMailClick(getGmailLink())}
            className="flex items-center gap-2 bg-white py-2 px-4 text-black rounded-lg shadow hover:bg-gray-100 transition"
          >
            <img
              src="https://www.svgrepo.com/show/303161/gmail-icon-logo.svg"
              alt="Gmail"
              className="w-5 h-5"
            />
            <span>Open Gmail</span>
          </button>

          <button
            onClick={() => handleMailClick(getOutlookLink())}
            className="flex items-center gap-2 bg-white py-2 px-4 text-black rounded-lg shadow hover:bg-gray-100 transition"
          >
            <img
              src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@latest/icons/microsoftoutlook.svg"
              alt="Outlook"
              className="w-5 h-5"
              style={{
                filter:
                  "invert(32%) sepia(93%) saturate(2558%) hue-rotate(186deg) brightness(97%) contrast(103%)",
              }}
            />
            <span>Open Outlook</span>
          </button>
        </div>
      </div>

     
      <div className="mt-6">
        <h6 className="font-semibold text-lg text-darkBlue">Option 2 — Manual</h6>
        <p className="text-gray-700 mt-2 flex flex-wrap items-center gap-1">
          If none of the options work, manually send an email from{" "}
          <strong>{email}</strong> to{" "}
          <span className="flex items-center gap-1">
            <strong className="text-primary">verify@testassessify.com</strong>
            {copiedEmail ? (
              <CheckCircleIcon
                className="w-5 h-5 text-primary transition-transform duration-300"
                title="Copied!"
              />
            ) : (
              <ClipboardDocumentIcon
                className="w-5 h-5 text-gray-600 cursor-pointer hover:text-primary"
                onClick={handleCopyEmail}
                title="Copy email address"
              />
            )}
          </span>{" "}
          with the above code.
        </p>
      </div>

      <div className="mt-4 flex items-center">
        <p className="font-semibold text-primary">✅ After Sending</p>
        <p className="text-gray-700 mt-1 pl-2">Once you’ve sent the mail, login again.</p>
      </div>
      { showSkip && <div className="flex justify-end mt-2 mb-0">
        <button data-test-id="skip-email-verification" onClick={handleSkip} className="py-3 px-5 mb-0 bg-slate-300 rounded-lg">
           Skip For Now
        </button>
      </div>}
    </NotificationModal>
  );
};

export default EmailVerificationModal;

